import type { StationPhoto } from '../api/types'

/** The photos loaded so far, one of each, newest first.
 *
 *  The panel holds two sources at once — the newest page, re-asked on a timer,
 *  and whatever older pages the reader stepped back into — and a refresh must
 *  neither duplicate a photo nor lose the ones already paged in. Photo ids are
 *  the listener's capture names, which sort by their UTC stamp, so ordering by
 *  id is ordering by arrival. Returns a new array.
 */
export function mergePhotos(...pages: StationPhoto[][]): StationPhoto[] {
  const byId = new Map<string, StationPhoto>()
  for (const page of pages) for (const photo of page) byId.set(photo.id, photo)
  return [...byId.values()].sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0))
}

/** Where a step leaves the reader: `+1` is older, `-1` newer, `0` stay.
 *
 *  Tracked by id, never by position: a new photo arriving while someone is
 *  looking at last night's would otherwise shift the list under them and
 *  swap the picture they were reading. `null` means "the newest". `needMore`
 *  asks for the next older page when the reader steps past the oldest loaded.
 */
export function stepPhoto(photos: StationPhoto[], selected: string | null,
                          step: -1 | 0 | 1): { id: string | null; needMore: boolean } {
  const at = selected === null ? 0 : Math.max(0, photos.findIndex((p) => p.id === selected))
  const here = photos[at]
  if (!here) return { id: null, needMore: false }
  const there = photos[Math.max(0, at + step)]
  if (!there) return { id: here.id, needMore: true }
  return { id: there.id, needMore: false }
}

/** Two hours: the logger sends one an hour, so one missed upload is normal. */
const PHOTO_GRACE_MS = 2 * 60 * 60 * 1000

/** A sentence when the newest photo is older than the logger's cadence
 *  explains, else null. Words, like the readings panel: a photo that looks
 *  current and is a day old reads as the river now. */
export function photoAgeNote(receivedAt: string, now: Date = new Date()): string | null {
  const age = now.getTime() - new Date(receivedAt).getTime()
  if (Number.isNaN(age) || age <= PHOTO_GRACE_MS) return null
  const hours = Math.floor(age / 3_600_000)
  if (hours < 48) return `No new photo for ${hours} hours`
  return `No new photo for ${Math.floor(hours / 24)} days`
}
